import axios from 'axios';
import logger from '../utils/logger';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:8000/api/',
  headers: { 'Content-Type': 'application/json' }
});

// Add request interceptor to include JWT token and handle FormData
api.interceptors.request.use(
  (config) => {
    const authTokens = localStorage.getItem('authTokens');
    if (authTokens) {
      const { access } = JSON.parse(authTokens);
      if (access) {
        config.headers.Authorization = `Bearer ${access}`;
      }
    }

    // Remove Content-Type for FormData so browser can set it with boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    // Normalize URL boundary so `baseURL` ending with `/` AND `url`
    // starting with `/` never produces `http://host/api//v1/...`.
    // Django tolerates the double slash for most GETs but CORS preflight
    // on POSTs fails (the 404 from the routing miss has no
    // Access-Control-Allow-Origin), which the browser reports as a CORS
    // error — really a routing miss. Pick the canonical form: baseURL
    // without trailing slash, url with leading slash.
    if (typeof config.baseURL === 'string') {
      config.baseURL = config.baseURL.replace(/\/+$/, '');
    }
    if (typeof config.url === 'string' && !config.url.startsWith('http')) {
      if (!config.url.startsWith('/')) config.url = '/' + config.url;
      // Also dedupe any internal double slashes that crept into the path
      // itself (rare, but cheap).
      config.url = config.url.replace(/\/{2,}/g, '/');
    }

    return config;
  },
  (error) => Promise.reject(error)
);


let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response.data,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;
    const data = error.response?.data;
    let message = 'Something went wrong';

    // Always log a one-line tag with the failing METHOD + URL + status so
    // generic "Something went wrong" messages aren't dead-end debugging.
    // Cheap, in dev only — strip in prod via terser dead-code if needed.
    if (process.env.NODE_ENV !== 'production') {
      const method = (originalRequest?.method || 'GET').toUpperCase();
      const url = originalRequest?.url || '<unknown url>';
      const baseURL = originalRequest?.baseURL || '';
      // Never log the Authorization header — a live JWT in the console is
      // readable by any extension with console access.
      const { Authorization: _omitted, ...safeHeaders } = originalRequest?.headers || {};
      const safeRequest = originalRequest ? { ...originalRequest, headers: safeHeaders } : originalRequest;
      // eslint-disable-next-line no-console
      logger.error(
        `[api] ${method} ${baseURL}${url} → ${status || 'network error'}`,
        // error itself is omitted: error.config carries the auth header.
        { responseData: data, request: safeRequest, errorMessage: error?.message },
      );
    }

    // Handle 401 Unauthorized - attempt token refresh
    if (status === 401 && !originalRequest._retry) {
      const isAuthRequest = originalRequest?.url?.includes('/auth/login') ||
                           originalRequest?.url?.includes('/auth/register') ||
                           originalRequest?.url?.includes('/auth/google') ||
                           originalRequest?.url?.includes('/auth/token/refresh');

      if (!isAuthRequest) {
        const authTokens = localStorage.getItem('authTokens');
        const tokens = authTokens ? JSON.parse(authTokens) : null;

        if (tokens?.refresh) {
          if (isRefreshing) {
            // Queue this request until refresh completes
            return new Promise((resolve, reject) => {
              failedQueue.push({ resolve, reject });
            }).then((newToken) => {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              return api(originalRequest);
            });
          }

          originalRequest._retry = true;
          isRefreshing = true;

          try {
            const response = await axios.post(
              `${api.defaults.baseURL}v1/auth/token/refresh/`,
              { refresh: tokens.refresh },
              { headers: { 'Content-Type': 'application/json' } }
            );

            const newAccess = response.data.access;
            // Refresh tokens rotate (the old one is blacklisted), so keep the new one.
            const updatedTokens = { ...tokens, access: newAccess, ...(response.data.refresh ? { refresh: response.data.refresh } : {}) };
            localStorage.setItem('authTokens', JSON.stringify(updatedTokens));

            processQueue(null, newAccess);
            originalRequest.headers.Authorization = `Bearer ${newAccess}`;
            return api(originalRequest);
          } catch (refreshError) {
            processQueue(refreshError, null);
            window.dispatchEvent(new CustomEvent('auth:session-expired'));
            return Promise.reject(refreshError);
          } finally {
            isRefreshing = false;
          }
        } else {
          window.dispatchEvent(new CustomEvent('auth:session-expired'));
        }
      }
    }

    // Last-resort message when no DRF field/detail/non_field_errors matches
    // — surface the failing endpoint + status code so the user can act on
    // it (e.g. tell us which call broke). Beats the opaque "Something went
    // wrong" string we used to show for 500s and CORS-shaped failures.
    if (!data || (typeof data === 'string' && !data.trim())) {
      const method = (originalRequest?.method || 'GET').toUpperCase();
      const path = (originalRequest?.url || '').replace(/^\/+|\/+$/g, '') || '<unknown>';
      const last = path.split('/').slice(-2).join('/');
      if (status) {
        message = `Request failed (${status}) on ${method} /${last}`;
      } else {
        message = `Couldn't reach the server (${method} /${last}). Network or backend down.`;
      }
    }

    if (data) {
      // Check for custom 'error' key
      if (data.error) {
        message = data.error;
      }
      // Check for DRF 'detail' key
      else if (data.detail) {
        message = data.detail;
      }
      // Check for DRF 'non_field_errors'
      else if (data.non_field_errors) {
        message = Array.isArray(data.non_field_errors)
          ? data.non_field_errors[0]
          : data.non_field_errors;
      }
      // Check for DRF field validation errors (e.g., {email: ["error"]})
      else if (typeof data === 'object') {
        const fieldErrors = Object.entries(data)
          .filter(([key, value]) => key !== 'email_exists' && (Array.isArray(value) || typeof value === 'string'))
          .map(([field, errors]) => {
            let errorMsg = Array.isArray(errors) ? errors[0] : errors;
            // Nested serializer errors (e.g. members/pools on Care Fund setup)
            // can be objects or arrays, not strings — coerce to a string so we
            // never call string methods on a non-string.
            if (typeof errorMsg !== 'string') {
              if (errorMsg && typeof errorMsg === 'object') {
                const nested = Object.values(errorMsg).flat()[0];
                errorMsg = typeof nested === 'string' ? nested : JSON.stringify(errorMsg);
              } else {
                errorMsg = String(errorMsg ?? '');
              }
            }
            // Capitalize field name and format message
            const fieldName = field.replace(/_/g, ' ');
            const capitalizedField = fieldName.charAt(0).toUpperCase() + fieldName.slice(1);
            // If error already mentions the field, just return it
            if (errorMsg.toLowerCase().includes(field.toLowerCase())) {
              return errorMsg.charAt(0).toUpperCase() + errorMsg.slice(1);
            }
            return `${capitalizedField}: ${errorMsg}`;
          });
        if (fieldErrors.length > 0) {
          message = fieldErrors.join('. ');
        }
      }
    }

    // Create error with additional properties
    const err = new Error(message);
    // Attach HTTP status + response payload so callers can branch on them
    // (e.g. 404 = "not found yet" rather than a real error to display).
    if (status) {
      err.status = status;
      err.response = { status, data };
    }
    // Attach additional flags from response data
    if (data?.email_exists) {
      err.email_exists = true;
    }
    return Promise.reject(err);
  }
);

export default api;
